import React, { useRef, useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { GripVertical } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { StaffMember } from './types';
import DraggableEvent from './DraggableEvent';
import { useAuth } from '@/contexts/AuthContext';
import { useUsers } from '@/contexts/UsersContext';
import { useEvents } from '@/contexts/EventsContext';
import { DEFAULT_CLOSE_TIME } from '@/lib/constants';

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const timeStrToHour = (t: string): number => {
    if (!t) return 0;
    const parts = t.slice(0, 5).split(':').map(Number);
    return parts[0] + (parts[1] || 0) / 60;
};

interface CalendarGridProps {
    view: 'day' | 'week';
    weekDays: Date[];
    hours: number[];
    startHour: number;
    endHour: number;
    displayEvents: any[];
    currentDate: Date;
    onEventClick: (booking: any) => void;
    onGroupClick?: (groupedEvent: any) => void;
    onEmptySlotClick?: (date: Date, time: string, staffId?: string) => void;
    onEventVerticalDrop?: (booking: any, newStartHour: number) => Promise<void>;
    onEventHorizontalDrop?: (booking: any, newStaffId?: string, newDate?: string) => Promise<void>;
    onEventDrop2D?: (booking: any, newStaffId?: string, newDate?: string, newStartHour?: number) => Promise<void>;
    t: any;
    selectedStaff?: StaffMember | null;
    staffMembers?: StaffMember[];
    step?: number;
    scrollToHour?: { value: number, _t: number } | null;
    /** Modalità spostamento: range liberi (verdi) per colonna dove l'appuntamento può atterrare */
    moveTargets?: { start: number; end: number }[][] | null;
    /** Modalità cancellati: prenotazioni cancellate da mostrare in overlay */
    cancelledEvents?: { id: number; colIndex: number; startHour: number; duration: number; clientName: string; cancelledAt: string | null }[] | null;
    /** Agenda in sola lettura (modalità cancellati) */
    readOnly?: boolean;
    /** L'admin può trascinare le intestazioni per riordinare le colonne */
    canReorderStaff?: boolean;
    onStaffReorder?: (orderedIds: string[]) => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
    view, weekDays, hours, startHour, endHour, displayEvents, currentDate, onEventClick, onGroupClick, onEmptySlotClick, t,
    selectedStaff, staffMembers = [], onEventVerticalDrop, onEventHorizontalDrop, onEventDrop2D, step = 30, scrollToHour = null,
    moveTargets = null, cancelledEvents = null, readOnly = false,
    canReorderStaff = false, onStaffReorder
}) => {
    const { organization } = useAuth();
    const { users } = useUsers();
    const { busySlots, shiftChanges } = useEvents();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const mainAreaRef = useRef<HTMLDivElement>(null);
    const [now, setNow] = useState(new Date());
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Una riga = uno slot dello step del salone, sempre della stessa altezza (come nel
    // mockup): così la giornata sta in una schermata invece di richiedere scroll continui.
    const ROW_HEIGHT = 44;
    const HOUR_HEIGHT = Math.round((ROW_HEIGHT * 60) / step);
    const MIN_COLUMN_WIDTH = 200;

    const isParallelView = view === 'day' && selectedStaff?.id === 'all';
    const activeStaff = staffMembers;

    // Riordino colonne: si trascina solo l'intestazione, quindi non interferisce
    // col drag&drop degli appuntamenti (che vive nel corpo della griglia).
    const [draggedStaffId, setDraggedStaffId] = useState<string | null>(null);
    const [dropTargetStaffId, setDropTargetStaffId] = useState<string | null>(null);
    const canDragStaff = canReorderStaff && isParallelView && activeStaff.length > 1 && !!onStaffReorder;

    const handleStaffDrop = (targetId: string) => {
        const sourceId = draggedStaffId;
        setDraggedStaffId(null);
        setDropTargetStaffId(null);
        if (!sourceId || sourceId === targetId || !onStaffReorder) return;

        const ids = activeStaff.map(s => s.id);
        const from = ids.indexOf(sourceId);
        const to = ids.indexOf(targetId);
        if (from === -1 || to === -1) return;

        ids.splice(to, 0, ids.splice(from, 1)[0]);
        onStaffReorder(ids);
    };

    // Calcolo layout o raggruppamento
    const { processedEvents, layoutMap } = React.useMemo(() => {
        const layout: Record<string, { left: number; width: number }> = {};
        const finalEvents: any[] = [];
        const eventsByColumn: Record<string, any[]> = {};

        displayEvents.forEach(e => {
            const colKey = isParallelView ? String(e.staffId) : String(e.dayIndex ?? 0);
            if (!eventsByColumn[colKey]) eventsByColumn[colKey] = [];
            eventsByColumn[colKey].push(e);
        });

        let groupIdCounter = 0;

        Object.keys(eventsByColumn).forEach(colKey => {
            const colEvents = eventsByColumn[colKey];
            colEvents.sort((a, b) => {
                if (a.startHour !== b.startHour) return a.startHour - b.startHour;
                return b.duration - a.duration;
            });

            const clusters: any[][] = [];
            let currentCluster: any[] = [];
            let clusterEnd = -1;

            colEvents.forEach(event => {
                if (currentCluster.length === 0) {
                    currentCluster.push(event);
                    clusterEnd = event.startHour + event.duration;
                } else if (event.startHour >= clusterEnd) {
                    clusters.push(currentCluster);
                    currentCluster = [event];
                    clusterEnd = event.startHour + event.duration;
                } else {
                    currentCluster.push(event);
                    clusterEnd = Math.max(clusterEnd, event.startHour + event.duration);
                }
            });
            if (currentCluster.length > 0) clusters.push(currentCluster);

            clusters.forEach(cluster => {
                const overlaps = (a: any, b: any) => a.startHour < b.startHour + b.duration && b.startHour < a.startHour + a.duration;
                // Copie di lavoro (non muto gli oggetti originali tra un re-render e l'altro)
                const work = cluster.map((e: any) => ({ ...e }));
                const confirmedEvents = work.filter((e: any) => e.status !== 'no_show');
                const noShowEvents = work.filter((e: any) => e.status === 'no_show');

                // Un no-show sovrapposto ad almeno un appuntamento confermato non occupa una colonna:
                // viene collassato in un pallino sull'appuntamento (evita la grafica "sdoppiata").
                noShowEvents.forEach((ns: any) => {
                    confirmedEvents.filter((c: any) => overlaps(ns, c)).forEach((c: any) => {
                        if (!c.noShowOverlaps) c.noShowOverlaps = [];
                        if (ns.booking) c.noShowOverlaps.push(ns.booking);
                    });
                });
                // I no-show che non si sovrappongono a nessun confermato restano blocchi (grigi) a sé.
                const standaloneNoShow = noShowEvents.filter((ns: any) => !confirmedEvents.some((c: any) => overlaps(ns, c)));

                const layoutEvents = [...confirmedEvents, ...standaloneNoShow].sort((a: any, b: any) => {
                    if (a.startHour !== b.startHour) return a.startHour - b.startHour;
                    return b.duration - a.duration;
                });
                if (layoutEvents.length === 0) return;

                const columns: any[][] = [];
                layoutEvents.forEach(event => {
                    let placed = false;
                    for (let i = 0; i < columns.length; i++) {
                        const col = columns[i];
                        const lastEvent = col[col.length - 1];
                        const lastEnd = lastEvent.startHour + lastEvent.duration;
                        if (event.startHour >= lastEnd) {
                            col.push(event);
                            placed = true;
                            break;
                        }
                    }
                    if (!placed) columns.push([event]);
                });

                const totalCols = columns.length;
                // Only group in week view
                const shouldGroup = view === 'week';
                const maxParallel = isMobile ? 1 : 2;

                if (shouldGroup && totalCols > maxParallel) {
                    const groupStart = Math.min(...layoutEvents.map(e => e.startHour));
                    const groupEnd = Math.max(...layoutEvents.map(e => e.startHour + e.duration));

                    const groupedEvent = {
                        id: `group-${groupIdCounter++}`,
                        isGroup: true,
                        count: layoutEvents.length,
                        startHour: groupStart,
                        duration: Math.max(groupEnd - groupStart, 0.5),
                        staffId: layoutEvents[0].staffId,
                        dayIndex: layoutEvents[0].dayIndex,
                        title: t('calendar.n_appointments', { count: layoutEvents.length }),
                        client: t('calendar.more_than_n_parallel', { count: maxParallel }),
                        color: layoutEvents[0].color,
                        booking: null,
                        events: layoutEvents
                    };
                    finalEvents.push(groupedEvent);
                    layout[groupedEvent.id] = { left: 0, width: 1 };
                } else {
                    // Manteniamo individuali se <= maxParallel
                    columns.forEach((col, colIndex) => {
                        const width = 1 / totalCols;
                        const left = colIndex * width;
                        col.forEach(event => {
                            finalEvents.push(event);
                            layout[event.id] = { left, width };
                        });
                    });
                }
            });
        });

        return { processedEvents: finalEvents, layoutMap: layout };
    }, [displayEvents, isParallelView, isMobile, view, t]);

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(interval);
    }, []);

    const currentHour = now.getHours() + now.getMinutes() / 60;
    const isBusinessHours = currentHour >= startHour && currentHour <= (hours[hours.length - 1] + 1);
    const currentLineTop = (currentHour - startHour) * HOUR_HEIGHT;

    const isTodayDisplayed = (checkDate: Date) => {
        const today = new Date();
        return checkDate.getDate() === today.getDate() &&
            checkDate.getMonth() === today.getMonth() &&
            checkDate.getFullYear() === today.getFullYear();
    };

    const showRedLineInDayView = view === 'day' && isTodayDisplayed(currentDate) && isBusinessHours;
    const todayWeekIndex = weekDays.findIndex(d => isTodayDisplayed(d));

    useEffect(() => {
        // Centra la visualizzazione sull'orario corrente al caricamento
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;

            const targetHour = Math.max(currentHour, startHour);
            const targetLineTop = (targetHour - startHour) * HOUR_HEIGHT;

            // Ritarda leggermente lo scroll per garantire che l'altezza del contenitore sia calcolata
            setTimeout(() => {
                const containerHeight = container.clientHeight;
                const targetScrollTop = Math.max(0, targetLineTop - containerHeight / 2);
                container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
            }, 100);
        }
    }, [selectedStaff, view]); // Eseguito al caricamento o quando cambia dipendente/vista

    // Scroll to a specific hour when requested (e.g. from search result click)
    useEffect(() => {
        if (!scrollToHour || !scrollContainerRef.current) return;
        const container = scrollContainerRef.current;
        setTimeout(() => {
            const targetLineTop = (scrollToHour.value - startHour) * HOUR_HEIGHT;
            const containerHeight = container.clientHeight;
            const targetScrollTop = Math.max(0, targetLineTop - containerHeight / 2);
            container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        }, 120);
    }, [scrollToHour]);

    const formatTime = (h: number) => {
        const hour = Math.floor(h);
        const min = Math.round((h % 1) * 60);
        return `${hour}:${min < 10 ? '00' : min}`;
    };

    const dayHeaderFormatter = new Intl.DateTimeFormat('it-IT', { weekday: 'short', day: 'numeric' });

    // Calculate Grid Width for Parallel View
    const gridWidthStyle = isParallelView
        ? { minWidth: `${Math.max(100, activeStaff.length * MIN_COLUMN_WIDTH)}px`, width: '100%' }
        : { width: '100%' };

    const columnCount = isParallelView ? activeStaff.length : (view === 'week' ? weekDays.length : 1);

    // Step-based grid slots: one entry per step interval across the visible hour range
    const stepHoursFraction = step / 60; // e.g. 0.25 for 15min, 0.5 for 30min
    const stepSlots = useMemo(() => {
        const slots: number[] = [];
        // Use integer arithmetic (in step units) to avoid float drift
        const totalSteps = Math.round((endHour - startHour) / stepHoursFraction);
        for (let i = 0; i < totalSteps; i++) {
            slots.push(startHour + i * stepHoursFraction);
        }
        return slots;
    }, [startHour, endHour, stepHoursFraction]);
    const stepSlotHeight = stepHoursFraction * HOUR_HEIGHT; // px per slot

    // --- Unavailability overlays ---
    // Returns [{start, end}] (in hours) of unavailable ranges for a given staff+day combination.
    // If the staff has a shift change for that day, it replaces their normal schedule.
    const getUnavailableRanges = useMemo(() => (
        staffId: string | null,
        dayDate: Date
    ): { start: number; end: number }[] => {
        // Data not yet loaded — show no overlays
        if (!organization?.time_ranges) return [];

        const dayName = DAY_NAMES[dayDate.getDay()];
        const shopSched = organization.time_ranges[dayName];
        const dStr = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;

        const shopOpen = shopSched?.open ? timeStrToHour(shopSched.open) : startHour;
        const shopClose = shopSched?.close ? timeStrToHour(shopSched.close) : endHour;
        const shopBreakStart = shopSched?.start_break ? timeStrToHour(shopSched.start_break) : null;
        const shopBreakEnd = shopSched?.end_break ? timeStrToHour(shopSched.end_break) : null;

        const ranges: { start: number; end: number }[] = [];

        if (staffId) {
            // Il cambio turno prevale su tutto: bypassa l'orario del salone, funziona anche su giorni chiusi
            const shiftChange = shiftChanges?.find((sc: any) =>
                sc.shift_date === dStr && sc.hairdresser_id === staffId
            );

            if (shiftChange) {
                const scOpen = timeStrToHour(shiftChange.start_time);
                const scClose = timeStrToHour(shiftChange.end_time);
                const scBreakStart = shiftChange.start_break ? timeStrToHour(shiftChange.start_break) : null;
                const scBreakEnd = shiftChange.end_break ? timeStrToHour(shiftChange.end_break) : null;

                if (startHour < scOpen) ranges.push({ start: startHour, end: scOpen });
                if (endHour > scClose) ranges.push({ start: scClose, end: endHour });
                if (scBreakStart !== null && scBreakEnd !== null && scBreakEnd > scBreakStart) {
                    ranges.push({ start: Math.max(startHour, scBreakStart), end: Math.min(endHour, scBreakEnd) });
                }
                return ranges.filter(r => r.end > r.start && r.start < endHour && r.end > startHour);
            }

            // Nessun cambio turno: se salone chiuso → tutto grigio
            if (!shopSched || !shopSched.open) {
                return [{ start: startHour, end: endHour }];
            }

            // Orario normale staff intersecato con salone
            const staffUser = users?.find((u: any) => u.id === staffId);
            const staffSched = staffUser?.time_ranges?.[dayName];

            if (!staffSched || !staffSched.open) {
                return [{ start: startHour, end: endHour }];
            }

            const effectiveOpen = timeStrToHour(staffSched.open);
            const effectiveClose = timeStrToHour(staffSched.close || DEFAULT_CLOSE_TIME);
            const effectiveBreakStart = staffSched.start_break ? timeStrToHour(staffSched.start_break) : shopBreakStart;
            const effectiveBreakEnd = staffSched.end_break ? timeStrToHour(staffSched.end_break) : shopBreakEnd;

            const openH = Math.max(shopOpen, effectiveOpen);
            const closeH = Math.min(shopClose, effectiveClose);

            if (startHour < openH) ranges.push({ start: startHour, end: openH });
            if (endHour > closeH) ranges.push({ start: closeH, end: endHour });

            if (effectiveBreakStart !== null && effectiveBreakEnd !== null && effectiveBreakEnd > effectiveBreakStart) {
                ranges.push({ start: Math.max(startHour, effectiveBreakStart), end: Math.min(endHour, effectiveBreakEnd) });
            }
        } else {
            // Nessuno staff specifico (vista "Tutto lo staff")
            if (!shopSched || !shopSched.open) {
                // Salone chiuso: grigio tranne dove qualche staff ha un cambio turno
                const dayShiftChanges = (shiftChanges || []).filter((sc: any) => sc.shift_date === dStr);
                if (dayShiftChanges.length === 0) {
                    return [{ start: startHour, end: endHour }];
                }
                // Calcola l'unione degli orari dei cambi turno, poi inverti per ottenere le fasce grigie
                const available = dayShiftChanges
                    .map((sc: any) => ({ start: timeStrToHour(sc.start_time), end: timeStrToHour(sc.end_time) }))
                    .sort((a: any, b: any) => a.start - b.start);
                let cursor = startHour;
                for (const r of available) {
                    if (r.start > cursor) ranges.push({ start: cursor, end: r.start });
                    cursor = Math.max(cursor, r.end);
                }
                if (cursor < endHour) ranges.push({ start: cursor, end: endHour });
            } else {
                // Salone aperto: usa orario salone
                if (startHour < shopOpen) ranges.push({ start: startHour, end: shopOpen });
                if (endHour > shopClose) ranges.push({ start: shopClose, end: endHour });
                if (shopBreakStart !== null && shopBreakEnd !== null && shopBreakEnd > shopBreakStart) {
                    ranges.push({ start: Math.max(startHour, shopBreakStart), end: Math.min(endHour, shopBreakEnd) });
                }
            }
        }

        return ranges.filter(r => r.end > r.start && r.start < endHour && r.end > startHour);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization, users, shiftChanges, startHour, endHour]);


    // Build per-column unavailability data
    // Returns array of (ranges[]) for each column index

    // Parse a date-string "YYYY-MM-DD[T...]" to midnight local Date
    const parseLocalMidnight = (dateStr: string): number => {
        const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
        return new Date(y, m - 1, d).getTime();
    };

    // True if a salon-wide holiday busy slot covers dayDate
    const isSalonHolidayForDay = (bs: any, dayDate: Date): boolean => {
        if (bs.type !== 'holiday' || bs.hairdresser_id) return false;
        const dayTs = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate()).getTime();
        const startTs = parseLocalMidnight(bs.start_date);
        const endTs = bs.end_date ? parseLocalMidnight(bs.end_date) : startTs;
        return dayTs >= startTs && dayTs <= endTs;
    };

    // Returns hour ranges where a specific staff member is on vacation / permit on dayDate
    // Also picks up salon-wide holidays (hairdresser_id = null)
    const getStaffBusyRanges = (staffId: string, dayDate: Date): { start: number; end: number }[] => {
        if (!busySlots?.length) return [];
        const dayTs = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate()).getTime();
        const result: { start: number; end: number }[] = [];

        for (const bs of busySlots) {
            if (bs.type !== 'permit' && bs.type !== 'holiday') continue;
            // Must affect this staff (or be salon-wide)
            if (bs.hairdresser_id && bs.hairdresser_id !== staffId) continue;

            const startTs = parseLocalMidnight(bs.start_date);
            const endTs = bs.end_date ? parseLocalMidnight(bs.end_date) : startTs;
            if (dayTs < startTs || dayTs > endTs) continue;

            if (bs.type === 'holiday' || bs.is_all_day || (!bs.start_time && !bs.end_time)) {
                result.push({ start: startHour, end: endHour });
            } else if (bs.start_time && bs.end_time && dayTs === startTs) {
                const bStart = timeStrToHour(bs.start_time);
                const bEnd = timeStrToHour(bs.end_time);
                const s = Math.max(startHour, bStart);
                const e = Math.min(endHour, bEnd);
                if (e > s) result.push({ start: s, end: e });
            }
        }
        return result;
    };

    // For weekly all-staff: grey a slot only if EVERY active staff member is unavailable.
    // Uses step-sized buckets for intersection.
    const getWeeklyAllStaffBusyRanges = (dayDate: Date): { start: number; end: number }[] => {
        // Salon holiday → full column grey
        if (busySlots?.some((bs: any) => isSalonHolidayForDay(bs, dayDate))) {
            return [{ start: startHour, end: endHour }];
        }
        if (!activeStaff.length) return [];

        const BUCKET = step / 60;
        const numBuckets = Math.round((endHour - startHour) / BUCKET);
        // allBusy[b] = true means every staff member is unavailable in bucket b
        const allBusy = new Array(numBuckets).fill(true);

        for (const s of activeStaff) {
            const busy = getStaffBusyRanges(s.id, dayDate);
            for (let b = 0; b < numBuckets; b++) {
                if (!allBusy[b]) continue; // already cleared
                const tStart = startHour + b * BUCKET;
                const tEnd = tStart + BUCKET;
                const isBusy = busy.some(r => r.start <= tStart && r.end >= tEnd);
                if (!isBusy) allBusy[b] = false;
            }
        }

        const ranges: { start: number; end: number }[] = [];
        let rs: number | null = null;
        for (let b = 0; b <= numBuckets; b++) {
            const t = startHour + b * BUCKET;
            if (b < numBuckets && allBusy[b] && rs === null) rs = t;
            if ((b === numBuckets || !allBusy[b]) && rs !== null) {
                ranges.push({ start: rs, end: t });
                rs = null;
            }
        }
        return ranges;
    };

    const mergeRanges = (a: { start: number; end: number }[], b: { start: number; end: number }[]) =>
        [...a, ...b].filter(r => r.end > r.start);

    // Finestre di disponibilità extra (type 'extra'): slot sbloccati a mano fuori orario.
    // staffId null = unione di tutti gli staff (viste "Tutto lo staff").
    const getExtraRanges = (staffId: string | null, dayDate: Date): { start: number; end: number }[] => {
        if (!busySlots?.length) return [];
        const dayTs = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate()).getTime();
        const result: { start: number; end: number }[] = [];
        for (const bs of busySlots) {
            if (bs.type !== 'extra') continue;
            if (staffId && bs.hairdresser_id !== staffId) continue;
            if (parseLocalMidnight(bs.start_date) !== dayTs) continue;
            if (!bs.start_time || !bs.end_time) continue;
            const s = Math.max(startHour, timeStrToHour(bs.start_time));
            const e = Math.min(endHour, timeStrToHour(bs.end_time));
            if (e > s) result.push({ start: s, end: e });
        }
        return result;
    };

    // Sottrae le finestre extra dalle fasce grigie dovute all'orario
    const subtractRanges = (
        ranges: { start: number; end: number }[],
        holes: { start: number; end: number }[]
    ): { start: number; end: number }[] => {
        if (!holes.length) return ranges;
        let out = ranges;
        for (const h of holes) {
            const next: { start: number; end: number }[] = [];
            for (const r of out) {
                if (h.end <= r.start || h.start >= r.end) { next.push(r); continue; }
                if (h.start > r.start) next.push({ start: r.start, end: h.start });
                if (h.end < r.end) next.push({ start: h.end, end: r.end });
            }
            out = next;
        }
        return out.filter(r => r.end > r.start);
    };

    // Blocchi manuali di slot singoli (type 'block'): sempre per-dipendente, con orari puntuali
    const getStaffBlockRanges = (staffId: string, dayDate: Date): { start: number; end: number }[] => {
        if (!busySlots?.length) return [];
        const dayTs = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate()).getTime();
        const result: { start: number; end: number }[] = [];
        for (const bs of busySlots) {
            if (bs.type !== 'block') continue;
            if (!bs.hairdresser_id || bs.hairdresser_id !== staffId) continue;
            if (parseLocalMidnight(bs.start_date) !== dayTs) continue;
            if (!bs.start_time || !bs.end_time) continue;
            const s = Math.max(startHour, timeStrToHour(bs.start_time));
            const e = Math.min(endHour, timeStrToHour(bs.end_time));
            if (e > s) result.push({ start: s, end: e });
        }
        return result;
    };

    const columnBlocks = useMemo(() => {
        if (isParallelView) {
            return activeStaff.map(s => getStaffBlockRanges(s.id, currentDate));
        }
        const staffId = selectedStaff && selectedStaff.id !== 'all' ? selectedStaff.id : null;
        if (!staffId) return view === 'week' ? weekDays.map(() => [] as { start: number; end: number }[]) : [[] as { start: number; end: number }[]];
        if (view === 'week') return weekDays.map(d => getStaffBlockRanges(staffId, d));
        return [getStaffBlockRanges(staffId, currentDate)];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isParallelView, view, activeStaff, weekDays, currentDate, selectedStaff, busySlots]);

    const columnUnavailability = useMemo(() => {
        // Il grigio da orario viene "bucato" dalle finestre extra; il grigio da
        // ferie/permessi invece resta (gli extra non scavalcano le assenze).
        if (isParallelView) {
            return activeStaff.map(s => {
                const sched = subtractRanges(getUnavailableRanges(s.id, currentDate), getExtraRanges(s.id, currentDate));
                return mergeRanges(sched, getStaffBusyRanges(s.id, currentDate));
            });
        }
        if (view === 'week') {
            const staffId = selectedStaff && selectedStaff.id !== 'all' ? selectedStaff.id : null;
            return weekDays.map(d => {
                const sched = subtractRanges(getUnavailableRanges(staffId, d), getExtraRanges(staffId, d));
                const busy = staffId ? getStaffBusyRanges(staffId, d) : getWeeklyAllStaffBusyRanges(d);
                return mergeRanges(sched, busy);
            });
        }
        // Day view (single column, specific staff selected)
        const staffId = selectedStaff && selectedStaff.id !== 'all' ? selectedStaff.id : null;
        const sched = subtractRanges(getUnavailableRanges(staffId, currentDate), getExtraRanges(staffId, currentDate));
        const busy = staffId ? getStaffBusyRanges(staffId, currentDate)
            : busySlots?.some((bs: any) => isSalonHolidayForDay(bs, currentDate))
                ? [{ start: startHour, end: endHour }]
                : [];
        return [mergeRanges(sched, busy)];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isParallelView, view, activeStaff, weekDays, currentDate, selectedStaff, getUnavailableRanges, busySlots, step, shiftChanges]);

    const hasMultipleColumns = isParallelView || view === 'week';
    const overlayColumnCount = hasMultipleColumns ? columnCount : 1;
    const viewKey = isParallelView ? 'parallel' : view;

    // Shared: resolves target column index from absolute pointer screen X
    const getTargetColIndex = (pointerX: number): number => {
        if (!mainAreaRef.current) return 0;
        const rect = mainAreaRef.current.getBoundingClientRect();
        return Math.floor(
            Math.max(0, Math.min(pointerX - rect.left, rect.width - 1)) / (rect.width / columnCount)
        );
    };

    // Shared helper: resolves newStaffId or newDate from an absolute pointer X position
    const resolveColumnDrop = (booking: any, pointerX: number): { newStaffId?: string; newDate?: string } => {
        const targetColIndex = getTargetColIndex(pointerX);

        if (isParallelView) {
            const currentColIndex = activeStaff.findIndex(s => s.id === booking.hairdresser_id);
            if (currentColIndex === -1) return {};
            const clamped = Math.max(0, Math.min(activeStaff.length - 1, targetColIndex));
            if (clamped === currentColIndex) return {};
            return { newStaffId: activeStaff[clamped].id };
        }

        if (view === 'week') {
            const bookingDate = new Date(booking.date);
            const currentDayIndex = weekDays.findIndex(d =>
                d.getFullYear() === bookingDate.getFullYear() &&
                d.getMonth() === bookingDate.getMonth() &&
                d.getDate() === bookingDate.getDate()
            );
            if (currentDayIndex === -1) return {};
            const clamped = Math.max(0, Math.min(weekDays.length - 1, targetColIndex));
            if (clamped === currentDayIndex) return {};
            const d = weekDays[clamped];
            return { newDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` };
        }

        return {};
    };

    // Returns pixel X offset from current column origin to target column origin (for optimistic snap)
    const computeSnapDx = (booking: any, pointerX: number): number => {
        if (!mainAreaRef.current) return 0;
        const rect = mainAreaRef.current.getBoundingClientRect();
        const colWidth = rect.width / columnCount;
        const targetColIndex = getTargetColIndex(pointerX);

        if (isParallelView) {
            const currentColIndex = activeStaff.findIndex(s => s.id === booking.hairdresser_id);
            if (currentColIndex === -1) return 0;
            return (Math.max(0, Math.min(activeStaff.length - 1, targetColIndex)) - currentColIndex) * colWidth;
        }

        if (view === 'week') {
            const bookingDate = new Date(booking.date);
            const currentDayIndex = weekDays.findIndex(d =>
                d.getFullYear() === bookingDate.getFullYear() &&
                d.getMonth() === bookingDate.getMonth() &&
                d.getDate() === bookingDate.getDate()
            );
            if (currentDayIndex === -1) return 0;
            return (Math.max(0, Math.min(weekDays.length - 1, targetColIndex)) - currentDayIndex) * colWidth;
        }

        return 0;
    };

    // Vertical-only drop (no column resolution needed — time is computed in DraggableEvent)
    const handleVerticalDrop = async (booking: any, newStartHour: number) => {
        if (onEventVerticalDrop) await onEventVerticalDrop(booking, newStartHour);
    };

    // Horizontal-only drop
    const handleHorizontalDrop = async (booking: any, pointerX: number) => {
        if (!onEventHorizontalDrop) return;
        const { newStaffId, newDate } = resolveColumnDrop(booking, pointerX);
        if (newStaffId || newDate) await onEventHorizontalDrop(booking, newStaffId, newDate);
    };

    // Combined 2D drop: column change + optional time change
    const handleDrop2D = async (booking: any, newStartHour: number | null, pointerX: number | null) => {
        const { newStaffId, newDate } = pointerX !== null ? resolveColumnDrop(booking, pointerX) : {};
        const hourChanged = newStartHour !== null;
        const colChanged = !!(newStaffId || newDate);
        if (!hourChanged && !colChanged) return;
        if (onEventDrop2D) {
            await onEventDrop2D(booking, newStaffId, newDate, hourChanged ? newStartHour! : undefined);
        }
    };

    return (
        <div ref={scrollContainerRef} className="flex-1 overflow-auto relative custom-scrollbar bg-white select-none">

            {/* HEADER Sticky Top */}
            <div className="sticky top-0 z-40 flex bg-white shadow-sm/50" style={gridWidthStyle}>
                {/* Corner spacer matches Time Axis width */}
                <div className="w-12 md:w-16 flex-shrink-0 bg-white border-r border-gray-200 z-40" />

                <div className="flex-1 relative overflow-hidden">
                    {/* Niente AnimatePresence mode="wait": con framer-motion da CDN l'uscita non
                        si completava mai, così passando a Settimana la vista restava vuota
                        (intestazioni, colonne ed eventi non venivano più montati). */}
                    <div className="h-full">
                        <motion.div
                            key={viewKey}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2 }}
                            className="grid divide-x divide-gray-200 h-full"
                            style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
                        >
                            {isParallelView ? (
                                // HEADER PARALLELO (STAFF)
                                activeStaff.map((staff) => {
                                    // Giornata chiusa: ferie di un solo giorno su currentDate
                                    const dayTs = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();
                                    const isDayClosed = (busySlots || []).some((bs: any) =>
                                        bs.type === 'holiday' && bs.hairdresser_id === staff.id &&
                                        parseLocalMidnight(bs.start_date) <= dayTs &&
                                        (bs.end_date ? parseLocalMidnight(bs.end_date) : parseLocalMidnight(bs.start_date)) >= dayTs);
                                    const isDragged = draggedStaffId === staff.id;
                                    const isDropTarget = dropTargetStaffId === staff.id && draggedStaffId !== staff.id;
                                    return (
                                        <div
                                            key={staff.id}
                                            draggable={canDragStaff}
                                            onDragStart={canDragStaff ? () => setDraggedStaffId(staff.id) : undefined}
                                            onDragEnd={canDragStaff ? () => { setDraggedStaffId(null); setDropTargetStaffId(null); } : undefined}
                                            onDragOver={canDragStaff ? (e) => { e.preventDefault(); setDropTargetStaffId(staff.id); } : undefined}
                                            onDragLeave={canDragStaff ? () => setDropTargetStaffId(prev => prev === staff.id ? null : prev) : undefined}
                                            onDrop={canDragStaff ? (e) => { e.preventDefault(); handleStaffDrop(staff.id); } : undefined}
                                            title={canDragStaff ? t('calendar.staff_order.hint', 'Trascina per cambiare l\'ordine delle colonne') : undefined}
                                            className={`group relative py-3 px-2 text-center flex flex-col items-center justify-center gap-0.5 min-w-0 transition-colors ${canDragStaff ? 'cursor-grab active:cursor-grabbing' : ''} ${isDragged ? 'opacity-40' : ''} ${isDropTarget ? 'bg-clip-50' : 'bg-white'}`}
                                        >
                                            {isDropTarget && (
                                                <span className="absolute left-0 top-1 bottom-1 w-0.5 bg-clip-500 rounded-full" />
                                            )}
                                            <div className="flex items-center justify-center gap-2 min-w-0">
                                                {canDragStaff && (
                                                    <GripVertical
                                                        size={13}
                                                        className="flex-shrink-0 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    />
                                                )}
                                                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${staff.color} overflow-hidden`}>
                                                    {staff.image ? (
                                                        <img src={staff.image} alt={staff.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        staff.avatar
                                                    )}
                                                </div>
                                                <span className="text-sm font-semibold truncate text-gray-700 min-w-0">
                                                    {staff.name}
                                                </span>
                                                {isDayClosed && (
                                                    <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wider bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                                                        {t('calendar.editday.closed_badge', 'Chiuso')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : view === 'week' ? (
                                // HEADER SETTIMANALE
                                weekDays.map((day, index) => {
                                    const isToday = isTodayDisplayed(day);
                                    return (
                                        <div key={index} className={`py-3 text-center ${isToday ? 'bg-clip-50/30' : 'bg-white'}`}>
                                            <span className={`text-[10px] md:text-sm font-semibold truncate px-1 block capitalize ${isToday ? 'text-clip-600' : 'text-gray-600'}`}>
                                                {dayHeaderFormatter.format(day)}
                                            </span>
                                            {isToday && <div className="mx-auto w-1.5 h-1.5 bg-clip-500 rounded-full mt-1"></div>}
                                        </div>
                                    );
                                })
                            ) : null}
                        </motion.div>
                    </div>
                </div>
            </div>

            <div className="flex relative" style={{ ...gridWidthStyle, height: `${(endHour - startHour) * HOUR_HEIGHT}px`, overflow: 'hidden' }}>

                {/* Time Axis (Sticky Left) */}
                <div className="w-12 md:w-16 flex-shrink-0 border-r border-gray-200 bg-white sticky left-0 z-10">
                    {stepSlots.map((slot) => {
                        const h = Math.floor(slot);
                        const m = Math.round((slot % 1) * 60);
                        const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                        const topOffset = (slot - startHour) * HOUR_HEIGHT;
                        return (
                            <div key={slot} className="absolute w-full text-[10px] md:text-xs text-gray-400 font-semibold text-right pr-1 pt-1" style={{ top: `${topOffset}px`, height: `${stepSlotHeight}px` }}>
                                <span className="block -translate-y-1/2 bg-white px-1 relative z-10 inline-block leading-none">{timeStr}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Main Area */}
                <div ref={mainAreaRef} className="flex-1 relative w-full">

                    {/* Background Grid: one row per step slot */}
                    <div className="absolute inset-0 pointer-events-none">
                        {stepSlots.map((slot) => {
                            const topOffset = (slot - startHour) * HOUR_HEIGHT;
                            return (
                                <div
                                    key={`grid-${slot}`}
                                    className="absolute w-full border-t border-gray-200"
                                    style={{ top: `${topOffset}px`, height: `${stepSlotHeight}px` }}
                                />
                            );
                        })}
                    </div>

                    {/* Unavailability overlays (behind events, above grid) */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{ zIndex: 1 }}
                    >
                        <div
                            className="absolute inset-0"
                            style={{ display: 'grid', gridTemplateColumns: `repeat(${overlayColumnCount}, 1fr)` }}
                        >
                            {columnUnavailability.map((ranges, colIdx) => (
                                <div key={`unavail-col-${colIdx}`} className="relative">
                                    {ranges.map((range, rangeIdx) => {
                                        // Giornata interamente chiusa: la fascia copre tutto l'orario visibile.
                                        const isFullDay = range.start <= startHour + 0.01 && range.end >= endHour - 0.01;
                                        return (
                                            <div
                                                key={`unavail-${colIdx}-${rangeIdx}`}
                                                className="absolute inset-x-0 bg-gray-200/70 border-y border-gray-300/40 flex justify-center"
                                                style={{
                                                    top: `${(range.start - startHour) * HOUR_HEIGHT}px`,
                                                    height: `${(range.end - range.start) * HOUR_HEIGHT}px`,
                                                }}
                                            >
                                                {isFullDay && (
                                                    <span className="mt-6 h-fit text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-white/80 px-2 py-0.5 rounded-full shadow-sm">
                                                        {t('calendar.closed', 'Chiuso')}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Blocchi manuali slot singoli (type 'block'): stesso grigio degli slot
                        non disponibili, così l'agenda mostra un solo tipo di indisponibilità */}
                    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }}>
                        <div
                            className="absolute inset-0"
                            style={{ display: 'grid', gridTemplateColumns: `repeat(${overlayColumnCount}, 1fr)` }}
                        >
                            {columnBlocks.map((ranges, colIdx) => (
                                <div key={`block-col-${colIdx}`} className="relative">
                                    {ranges.map((range, rangeIdx) => (
                                        <div
                                            key={`block-${colIdx}-${rangeIdx}`}
                                            className="absolute inset-x-0 bg-gray-200/70 border-y border-gray-300/40"
                                            style={{
                                                top: `${(range.start - startHour) * HOUR_HEIGHT}px`,
                                                height: `${(range.end - range.start) * HOUR_HEIGHT}px`,
                                            }}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Slot liberi (verdi) in modalità spostamento */}
                    {moveTargets && (
                        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }}>
                            <div
                                className="absolute inset-0"
                                style={{ display: 'grid', gridTemplateColumns: `repeat(${overlayColumnCount}, 1fr)` }}
                            >
                                {moveTargets.map((ranges, colIdx) => (
                                    <div key={`mt-col-${colIdx}`} className="relative">
                                        {ranges.map((range, rangeIdx) => (
                                            <div
                                                key={`mt-${colIdx}-${rangeIdx}`}
                                                className="absolute inset-x-0.5 bg-emerald-100/70 border border-emerald-300/70 rounded-md"
                                                style={{
                                                    top: `${(range.start - startHour) * HOUR_HEIGHT + 1}px`,
                                                    height: `${(range.end - range.start) * HOUR_HEIGHT - 2}px`,
                                                }}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Overlay prenotazioni cancellate (modalità "Cancellati") */}
                    {cancelledEvents && (
                        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 26 }}>
                            <div
                                className="absolute inset-0"
                                style={{ display: 'grid', gridTemplateColumns: `repeat(${overlayColumnCount}, 1fr)` }}
                            >
                                {Array.from({ length: overlayColumnCount }).map((_, colIdx) => (
                                    <div key={`cx-col-${colIdx}`} className="relative">
                                        {cancelledEvents.filter((e) => e.colIndex === colIdx).map((e) => (
                                            <div
                                                key={`cx-${e.id}`}
                                                className="absolute inset-x-1 bg-red-50 border-l-2 border border-red-200 rounded-md px-2 py-1 overflow-hidden"
                                                style={{
                                                    top: `${(e.startHour - startHour) * HOUR_HEIGHT + 1}px`,
                                                    height: `${Math.max(e.duration * HOUR_HEIGHT - 2, 34)}px`,
                                                }}
                                            >
                                                <p className="text-xs font-bold text-red-800 line-through truncate">{e.clientName}</p>
                                                <p className="text-[10px] text-red-500 truncate">
                                                    {e.cancelledAt
                                                        ? t('calendar.cancelled_at', 'Cancellato: {{when}}', { when: format(new Date(e.cancelledAt), 'd MMM, HH:mm', { locale: it }) })
                                                        : t('calendar.cancelled_label', 'Cancellato')}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Sola lettura (cancellati / chiudi giornata): intercetta click e drag sugli eventi */}
                    {readOnly && (
                        <div className="absolute inset-0 bg-white/40" style={{ zIndex: 25, pointerEvents: 'auto' }} />
                    )}

                    {/* Vertical Columns (Day/Staff separators) */}
                    <motion.div
                            key={`cols-${viewKey}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            className="absolute inset-0 grid divide-x divide-gray-200 pointer-events-auto cursor-pointer"
                            style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
                        >
                            {Array.from({ length: columnCount }).map((_, i) => (
                                <div
                                    key={`col-${i}`}
                                    className={`h-full ${view === 'week' && isTodayDisplayed(weekDays[i]) ? 'bg-clip-50/10' : ''}`}
                                    onClick={(e) => {
                                        if (!onEmptySlotClick) return;

                                        // 1. Calcola l'ora basata sull'Y del click
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const y = e.clientY - rect.top;

                                        const clickedHourFraction = startHour + (y / HOUR_HEIGHT);
                                        // Snap in base allo step (es. 15 min = 0.25 ore)
                                        const stepFraction = step / 60;
                                        const snappedHour = Math.floor(clickedHourFraction / stepFraction) * stepFraction;

                                        const finalHour = Math.max(startHour, Math.min(snappedHour, endHour - stepFraction));

                                        const h = Math.floor(finalHour);
                                        const m = Math.round((finalHour - h) * 60);
                                        const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;

                                        // 2. Calcola la Data o lo Staff
                                        let date = currentDate;
                                        let staffId: string | undefined = undefined;

                                        if (isParallelView) {
                                            // Parallel: i = indice staff, la data è currentDate
                                            staffId = activeStaff[i]?.id;
                                        } else if (view === 'week') {
                                            // Week: i = indice giorno della settimana
                                            date = weekDays[i];
                                            staffId = selectedStaff?.id === 'all' ? undefined : selectedStaff?.id;
                                        } else {
                                            // Day: i = 0, giorno singolo
                                            staffId = selectedStaff?.id === 'all' ? undefined : selectedStaff?.id;
                                        }

                                        onEmptySlotClick(date, timeStr, staffId);
                                    }}
                                ></div>
                            ))}
                    </motion.div>

                    {/* Current Time Line */}
                    {(showRedLineInDayView || (view === 'week' && todayWeekIndex !== -1 && isBusinessHours)) && !isParallelView && (
                        <div
                            className="absolute z-30 pointer-events-none flex items-center transition-all duration-300 ease-in-out"
                            style={{
                                top: `${currentLineTop}px`,
                                left: view === 'week' ? `${(todayWeekIndex / columnCount) * 100}%` : '0%',
                                width: view === 'week' ? `${100 / columnCount}%` : '100%',
                            }}
                        >
                            <div className="w-full border-t-2 border-clip-500 shadow-[0_0_8px_rgba(217,108,79,0.6)] relative flex items-center">
                                <div className="absolute -left-[6px] top-1/2 -translate-y-1/2 w-3 h-3 bg-clip-500 rounded-full border-2 border-white shadow-sm"></div>
                            </div>
                        </div>
                    )}

                    {/* Red Line for Parallel View (Full Width) */}
                    {isParallelView && isTodayDisplayed(currentDate) && isBusinessHours && (
                        <div
                            className="absolute z-30 pointer-events-none flex items-center w-full"
                            style={{ top: `${currentLineTop}px`, left: 0 }}
                        >
                            <div className="w-full border-t-2 border-clip-500 shadow-[0_0_8px_rgba(217,108,79,0.6)] relative flex items-center">
                                <div className="absolute -left-[6px] top-1/2 -translate-y-1/2 w-3 h-3 bg-clip-500 rounded-full border-2 border-white shadow-sm"></div>
                            </div>
                        </div>
                    )}

                    {/* Events */}
                    <motion.div
                            key={viewKey}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                            className="absolute inset-0 pointer-events-none"
                        >
                            {processedEvents.map(event => {
                                const top = (event.startHour - startHour) * HOUR_HEIGHT;
                                // Slot-fill: con dynamic_slots OFF il blocco riempie lo slot dello step
                                // (es. servizio da 15' in griglia da 30' occupa i 30'), pur mantenendo la durata reale.
                                const stepH = step / 60;
                                const visualDuration = (organization as any)?.dynamic_slots_enabled || event.isGroup
                                    ? event.duration
                                    : Math.max(stepH, Math.ceil((event.duration - 1e-6) / stepH) * stepH);
                                const height = visualDuration * HOUR_HEIGHT;

                                // CALCULATE LEFT & WIDTH
                                let left = 0;
                                let width = 100;

                                if (isParallelView) {
                                    // Find Staff Index
                                    const staffIndex = activeStaff.findIndex(s => s.id === event.staffId);
                                    if (staffIndex !== -1) {
                                        const colLeft = (staffIndex / activeStaff.length) * 100;
                                        const colWidth = (1 / activeStaff.length) * 100;

                                        const layout = layoutMap[event.id] || { left: 0, width: 1 };
                                        const eventWidth = event.status === 'no_show' ? Math.min(layout.width, 0.5) : layout.width;

                                        // Applichiamo la frazione sul singolo staff
                                        left = colLeft + (layout.left * colWidth);
                                        width = eventWidth * colWidth;
                                    }
                                } else {
                                    // Week/Day View Logic: Side-by-Side Overlaps
                                    const layout = layoutMap[event.id] || { left: 0, width: 1 };
                                    const eventWidth = event.status === 'no_show' ? Math.min(layout.width, 0.5) : layout.width;

                                    if (view === 'week') {
                                        const dayWidth = 100 / columnCount;
                                        const baseLeft = (event.dayIndex / columnCount) * 100;

                                        // Relative to day column
                                        left = baseLeft + (layout.left * dayWidth);
                                        width = eventWidth * dayWidth;
                                    } else {
                                        // Single Day View (Standard)
                                        left = layout.left * 100;
                                        width = eventWidth * 100;
                                    }
                                }

                                return (
                                    <DraggableEvent
                                        key={`${event.id}-${event.startHour}`}
                                        event={event}
                                        top={top}
                                        height={height}
                                        left={left}
                                        width={width}
                                        formatTime={formatTime}
                                        onEventClick={(booking: any) => {
                                            if (event.isGroup) {
                                                onGroupClick?.(event);
                                            } else {
                                                onEventClick(booking);
                                            }
                                        }}
                                        onEventVerticalDrop={handleVerticalDrop}
                                        onEventHorizontalDrop={(isParallelView || view === 'week') ? handleHorizontalDrop : undefined}
                                        onEventDrop2D={(isParallelView || view === 'week') ? handleDrop2D : undefined}
                                        getHorizontalSnapDx={(isParallelView || view === 'week') ? computeSnapDx : undefined}
                                        dragAxis={(isParallelView || view === 'week') ? 'xy' : 'y'}
                                        HOUR_HEIGHT={HOUR_HEIGHT}
                                        stepMinutes={step}
                                    />
                                );
                            })}
                    </motion.div>
                </div>
            </div>
        </div >
    );
};
export default CalendarGrid;
